import { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';

import { Select, Input, Radio, Button, Checkbox, Table, Row, Form, Skeleton, Modal, Result } from 'antd';
import Title from 'antd/lib/typography/Title';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { getDisplayCompanyId, numberInputOnly, phoneFormatInput, validateEmailAddress, validatePhoneNumber } from 'utils/helpers';

import { getAllDesc } from 'store/app/ipa/actions';
import { sendRegistration, sendRegistrationToCentral } from 'store/auth/actions';
import { getCompanies } from 'store/parameter/actions';

import FloatLabel from 'components/Common/FloatLabel';

import constants from 'constants/index';

import './index.scss';

const { Option } = Select;
const { TextArea } = Input;

type RegistrationDetailsProps = {
  getAllDesc(): any,
  sendRegistration(form: Object): any,
  isFetchingAll: boolean,
  isRegistering: boolean,
  setVisible(visible: boolean): void,
  selectedIpa: string,

  sendRegistrationToCentral(form: Object): any,
  isRegisteringCentral: boolean,
  getCompanies(): any,
};

const RegistrationDetails = (props: RegistrationDetailsProps) => {

  const [form] = Form.useForm();
  const [companyIdOptions, setCompanyIdOptions] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [checkedOther, setCheckedOther] = useState(false);
  const [multipleOffices, setMultipleOffices] = useState(false);
  const [outsideBiller, setOutsideBiller] = useState(false);
  const [accountType, setAccountType] = useState<any[]>([]);
  const [additionalVendorTaxId, setAdditionalVendorTaxId] = useState(false);
  const [officeTableDataSource, setOfficeTableDataSource] = useState<Object[]>([]);
  const [vendorTableDataSource, setVendorTableDataSource] = useState<Object[]>([]);

  const officeTableColumns = [
    {
      title: 'Address',
      key: 'Address',
      dataIndex: 'Address'
    },
    {
      title: 'City',
      key: 'City',
      dataIndex: 'City'
    },
    {
      title: 'Department',
      key: 'Department',
      dataIndex: 'Department'
    },
    {
      title: 'Contact Person',
      key: 'Contact',
      dataIndex: 'Contact'
    },
    {
      title: 'Contact Phone',
      key: 'PhoneNumber',
      dataIndex: 'PhoneNumber'
    },
    {
      title: 'Contact Email',
      key: 'Email',
      dataIndex: 'Email'
    },
    {
      title: '',
      key: 'Remove',
      dataIndex: 'Remove',
      render: (_: any, __: any, index: number) => <Button type="primary" onClick={() => removeFromTable('office', index)} danger>Remove</Button>
    }
  ];
  const additionalVendorTaxIdTableColumns = [
    {
      title: 'Vendor/Tax ID',
      key: 'Id',
      dataIndex: 'Id',
    },
    {
      title: 'Vendor/Group Name',
      key: 'Group',
      dataIndex: 'Group'
    },
    {
      title: '',
      key: 'Remove',
      dataIndex: 'Remove',
      render: (_: any, __: any, index: number) => <Button type="primary" onClick={() => removeFromTable('vendor', index)} danger>Remove</Button>
    }
  ];

  const fields = [
    'companyId',
    'vendorGroupName',
    'vendorTaxId',
    'primaryOfficeContactManager',
    'contactPhoneNumber',
    'contactPhoneNumberExt',
    'officeEmailAddress',
    'mainOfficeLocation',
    'secondaryAdmin',
    'comment',
    'accountType',
    'billingCompany',
    'billingContactPerson',
    'billingPhoneNumber',
    'billingPhoneNumberExt',
    'billingEmail'
  ];
  const officeFields = ['address', 'city', 'department', 'contactPerson', 'contactPhone', 'contactEmail'];
  const vendorFields = ['additionalVendorTaxId', 'additionalVendorGroupName'];

  useEffect(() => {
    getCompanies();
  }, []);

  const getCompanies = () => {
    props.getCompanies()
      .then((response: any[]) => {
        const filterIpaDropdownList = response?.filter(obj => 
            !constants.B2C_Register_IPAS.includes(obj.ipa) && 
            obj.ipa !== "LSMA" && 
            obj.isActive
        );
        setCompanyIdOptions(filterIpaDropdownList);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const onRegister = () => {

    let validationFields = fields;
    if (multipleOffices && officeTableDataSource.length === 0) validationFields = validationFields.concat(officeFields);
    if (additionalVendorTaxId && vendorTableDataSource.length === 0) validationFields = validationFields.concat(vendorFields);

    form.validateFields(validationFields).then((values:any) => {
      if (companyId.length === 0) {
        notifier.error('Please select at least one IPA.');
      }

      let phoneNumber = values.contactPhoneNumber?.replace(/[^A-Z0-9]/ig, '');
      let billingPhoneNumber = values.billingPhoneNumber?.replace(/[^A-Z0-9]/ig, '');

      if (values.contactPhoneNumberExt) {
        phoneNumber = phoneNumber + '-' + values.contactPhoneNumberExt;
      }

      if (values.billingPhoneNumberExt) {
        billingPhoneNumber = billingPhoneNumber + '-' + values.billingPhoneNumberExt;
      }

      const formData = {
        SelectedIpas: companyId,
        VendorName: values.vendorGroupName,
        VendorId: values.vendorTaxId,
        Contact: values.primaryOfficeContactManager,
        PhoneNumber: phoneNumber,
        Email: values.officeEmailAddress,
        User: values.secondaryAdmin || null,
        OfficeLocation: values.mainOfficeLocation,
        MultipleLocation: multipleOffices,
        Location: '/Register',
        Locations: officeTableDataSource,
        Comment: values.comment,
        AccountType: accountType.toString().split(',').join(', '),
        BillingAccess: outsideBiller,
        BillingName: values.billingCompany,
        BillingContact: values.billingContactPerson,
        BillingPhoneNumber: billingPhoneNumber,
        BillingEmail: values.billingEmail,
        MultipleVendor: additionalVendorTaxId,
        Vendors: vendorTableDataSource,
      };

      props.sendRegistration(formData)
        .then(() => {
          Modal.success({
            icon: false,
            width: '750px',
            content: <Result
              status="success"
              title="Thank you for registering"
              subTitle="We have received your application form and it will be reviewed by our team. Please allow 3 to 5 business days for your request to be processed. We will contact you through phone or email to distribute your account details. Please note that the processing time could be delayed if further verification of the application is needed. Thank you for contacting the web portal team."
            />,
          });
          closeModal();
        })
        .catch((err: ErrorResponse) => {
          notifier.error('Something went wrong, please contact a system administrator.');
        });

      props.sendRegistrationToCentral({ json_body: JSON.stringify(formData) });
    });
  };

  const pushToTable = (tableName: string) => {
    if (tableName === 'office') {
      form.validateFields(officeFields).then(values => {
        setOfficeTableDataSource(prev => [...prev, {
          Address: values.address,
          City: values.city,
          Department: values.department,
          Contact: values.contactPerson,
          PhoneNumber: values.contactPhone,
          Email: values.contactEmail,
        }]);
        form.resetFields(officeFields);
      });
    } else {
      form.validateFields(vendorFields).then(values => {
        setVendorTableDataSource(prev => [...prev, {
          Id: values.additionalVendorTaxId,
          Group: values.additionalVendorGroupName,
        }]);
        form.resetFields(vendorFields);
      })
    }
  };

  const removeFromTable = (tableName: string, index: number) => {
    if (tableName === 'office') setOfficeTableDataSource(prev => prev.filter((_, i) => i !== index));
    else setVendorTableDataSource(prev => prev.filter((_, i) => i !== index));
  };

  const validateCharacterLength = async (_: any, value: any) => {
    if (value && value.length < 2) {
      return Promise.reject(new Error('Please enter at least 2 characters.'))
    }
    return Promise.resolve();
  };

  const validateAccountType = async () => {
    if (accountType.length === 0) {
      return Promise.reject(new Error('Required.'))
    }
    return Promise.resolve();
  };

  const handleSelectChange = (value: any) => {
    setCompanyId(value.join(', '));
  };

  const closeModal = () => {
    props.setVisible(false);
    form.resetFields();
    setVendorTableDataSource([]);
    setOfficeTableDataSource([]);
    setAdditionalVendorTaxId(false);
    setMultipleOffices(false);
    setAccountType([]);
  }

  return (
    props.isRegistering || props.isFetchingAll ? (
      <>
        {props.isRegistering ?
          <Title level={5}>Processing your submission, please wait...</Title>
          :
          <Title level={5}>Loading...</Title>
        }
        <Skeleton
          active
          paragraph={{ rows: 4 }}
        />
        <br />
        <Skeleton
          active
          paragraph={{ rows: 8 }}
        />
      </>
    ) : (
    <>
      This registration form is intended for provider's office only. For all billing companies, please reach out to the provider's office to register for account on your behalf.
      <Form
        form={form}
        name='registrationForm'
        onFinish={onRegister}
      >
        <FloatLabel
          label="Select IPA(s)"
          name="companyId"
          form={form}
          required
        >
          <Select
            onChange={handleSelectChange}
            showSearch
            style={{ width: '100%' }}
            mode="multiple"
          >
            {companyIdOptions.map((option, index) => {
              return <Option key={index} value={option.ipa}>{getDisplayCompanyId(getDisplayCompanyId(option.ipa)  + ' - ' + option?.fullName?.toUpperCase())}</Option>
            })}
          </Select>
        </FloatLabel>

        <div className="row">
          <div className="col-lg-8">
            <FloatLabel
              label="Vendor/Group Name "
              name={"vendorGroupName"}
              form={form}
              required
              rules={[
                () => ({
                  validator: validateCharacterLength
                })
              ]}
            >
              <Input />
            </FloatLabel>
          </div>
          <div className="col-lg-8">
            <FloatLabel
              label="Vendor/Tax ID "
              name={"vendorTaxId"}
              form={form}
              required
              rules={[
                () => ({
                  validator: validateCharacterLength
                })
              ]}
            >
              <Input />
            </FloatLabel>
          </div>
          <div className="col-lg-8">
            <FloatLabel
              label="Primary Office Contact/Manager "
              name={"primaryOfficeContactManager"}
              form={form}
              required
              rules={[
                () => ({
                  validator: validateCharacterLength
                })
              ]}
            >
              <Input />
            </FloatLabel>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-4">
            <FloatLabel
              label="Contact Phone Number"
              name={"contactPhoneNumber"}
              form={form}
              required
              rules={[
                () => ({
                  validator: validatePhoneNumber
                })
              ]}
            >
              <Input type="tel" onKeyPress={(e: any) =>  form.setFieldsValue({ contactPhoneNumber: phoneFormatInput(e)} ) } />
            </FloatLabel>
          </div>
          <div className="col-lg-4">
            <FloatLabel
              label="Ext"
              name={"contactPhoneNumberExt"}
              form={form}
            >
              <Input type="tel" onKeyPress={(e: any) =>  form.setFieldsValue({ contactPhoneNumberExt: numberInputOnly(e)} ) } />
            </FloatLabel>
          </div>
          <div className="col-lg-8">
            <FloatLabel
              label="Office Email Address "
              name={"officeEmailAddress"}
              form={form}
              required
              rules={[
                () => ({
                  validator: validateEmailAddress
                })
              ]}
            >
              <Input type="email" />
            </FloatLabel>
          </div>
          <div className="col-lg-8">
            <FloatLabel
              label="Secondary Admin (if applicable)"
              name={"secondaryAdmin"}
              form={form}
            >
              <Input />
            </FloatLabel>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-24">
            <FloatLabel
              label="Main Office Location "
              name={"mainOfficeLocation"}
              form={form}
              required
            >
              <Input />
            </FloatLabel>
          </div>
        </div>
        <div>
        Provider offices may request for one account per office location/department. Will your office need to apply for more than one account?&nbsp;
          <Radio.Group value={multipleOffices} onChange={e => setMultipleOffices(e.target.value)}>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </div>
        {multipleOffices === true && (
          <div>
            If you are requesting accounts for additional office location/departments, please list all affiliated details under this vendor/group
            {officeTableDataSource?.length !== 0 &&
              <Table
                columns={officeTableColumns}
                dataSource={officeTableDataSource}
                rowKey={'Address'}
                pagination={false}
              />
            }
            <div className="row">
              <div className="col-lg-7">
                <FloatLabel
                  label="Address "
                  name={"address"}
                  form={form}
                  required
                >
                  <Input />
                </FloatLabel>
              </div>
              <div className="col-lg-3">
                <FloatLabel
                  label="City "
                  name={"city"}
                  form={form}
                  required
                >
                  <Input />
                </FloatLabel>
              </div>
              <div className="col-lg-3">
                <FloatLabel
                  label="Department"
                  name={"department"}
                  form={form}
                  required
                >
                  <Input />
                </FloatLabel>
              </div>
              <div className="col-lg-3">
                <FloatLabel
                  label="Contact Person "
                  name={"contactPerson"}
                  form={form}
                  required
                >
                  <Input />
                </FloatLabel>
              </div>
              <div className="col-lg-3">
                <FloatLabel
                  label="Contact Phone "
                  name={"contactPhone"}
                  form={form}
                  required
                  rules={[
                    () => ({
                      validator: validatePhoneNumber
                    })
                  ]}
                >
                  <Input type="tel" onKeyPress={(e: any) =>  form.setFieldsValue({ contactPhone: phoneFormatInput(e)} ) } />
                </FloatLabel>
              </div>
              <div className="col-lg-3">
                <FloatLabel
                  label="Contact Email "
                  name={"contactEmail"}
                  form={form}
                  required
                  rules={[
                    () => ({
                      validator: validateEmailAddress
                    })
                  ]}
                >
                  <Input type="email" />
                </FloatLabel>
              </div>
              {officeTableDataSource?.length < 20 &&
                <div className="btn-wrapper col-lg-2">
                  <Button type="primary" onClick={() => pushToTable('office')} style={{ width: '100%' }}>Add</Button>
                </div>
              }
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-lg-24">
            <FloatLabel
              label="Comment"
              name={"comment"}
              form={form}
            >
              <TextArea rows={4} />
            </FloatLabel>

          </div>
        </div>
        <b>
          **Please note that approval for portal access to eligibility, authorization, and claims are granted based on individual IPA policies**
        </b>
        <div>
          Please choose the following Account Type that your office is requesting for. (Select all that applies)
          <br /> <br />
          <label>Account Type <div style={{color: 'red', display: 'inline-block'}}>*</div>:</label>
          <FloatLabel
            style={{marginTop: "-25px"}}
            className="mb-1"
            label=""
            name="accountType"
            form={form}
            rules={[
              () => ({
                validator: validateAccountType
              })
            ]}
          >  <br/>
              <Checkbox.Group onChange={(value) => { setAccountType(value) }}>
              <Row><Checkbox value='Provider Account'>Provider Account</Checkbox></Row>
              <Row><Checkbox value='ACO Account'>ACO Account</Checkbox></Row>
              <Row><Checkbox value='Billing Account'>Billing Account</Checkbox></Row>
            </Checkbox.Group>
          </FloatLabel>
          {
          (accountType.includes('Billing Account')) ?
              <div className="col-lg-24">
                <div className="col-lg-24">
                <b>Please enter the outside billing company's information below. Please note all billing companies will need to contact main provider's office to fill out the registration from on their behalf.</b>
                </div>
                <div className="col-lg-6">
                  <FloatLabel
                    label="Billing Company "
                    name={"billingCompany"}
                    form={form}
                    required
                  >
                    <Input />
                  </FloatLabel>
                </div>
                <div className="col-lg-6">
                  <FloatLabel
                    label="Billing Contact Person "
                    name={"billingContactPerson"}
                    form={form}
                    required
                  >
                    <Input />
                  </FloatLabel>
                </div>
                <div className="col-lg-4">
                  <FloatLabel
                    label="Billing Phone Number "
                    name={"billingPhoneNumber"}
                    form={form}
                    required
                    rules={[
                      () => ({
                        validator: validatePhoneNumber
                      })
                    ]}
                  >
                    <Input type="tel" onKeyPress={(e: any) =>  form.setFieldsValue({ billingPhoneNumber: phoneFormatInput(e)} ) } />
                  </FloatLabel>
                </div>
                <div className="col-lg-4">
                  <FloatLabel
                    label="Ext "
                    name={"billingPhoneNumberExt"}
                    form={form}
                  >
                    <Input type="tel" onKeyPress={(e: any) =>  form.setFieldsValue({ billingPhoneNumberExt: numberInputOnly(e)} ) } />
                  </FloatLabel>
                </div>
                <div className="col-lg-6">
                  <FloatLabel
                    label="Billing Email "
                    name={"billingEmail"}
                    form={form}
                    required
                    rules={[
                      () => ({
                        validator: validateEmailAddress
                      })
                    ]}
                  >
                    <Input />
                  </FloatLabel>
                </div>
              </div>
            : null
          }
        </div>
        <div>
        Do you have additional Vendor/Tax ID? &nbsp;
          <Radio.Group value={additionalVendorTaxId} onChange={e => setAdditionalVendorTaxId(e.target.value)}>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </div>
        {additionalVendorTaxId === true && (
          <>
          {vendorTableDataSource?.length !== 0 &&
            <Table
              columns={additionalVendorTaxIdTableColumns}
              dataSource={vendorTableDataSource}
              pagination={false}
              rowKey={'Id'}
            />
          }
            <div className="row">
              <div className="col-lg-6">
                <FloatLabel
                  label="Vendor/Tax ID "
                  name={"additionalVendorTaxId"}
                  form={form}
                  required
                >
                  <Input />
                </FloatLabel>
              </div>
              <div className="col-lg-6">
                <FloatLabel
                  label="Vendor/Group Name "
                  name={"additionalVendorGroupName"}
                  form={form}
                  required
                >
                  <Input />
                </FloatLabel>
              </div>
              {vendorTableDataSource?.length < 20 &&
                <div className="btn-wrapper col-lg-2">
                  <Button type="primary" onClick={() => pushToTable('vendor')} style={{ width: '100%' }}>Add</Button>
                </div>
              }
            </div>
          </>
        )}
        <br />
        <div className="btn-wrapper d-flex justify-content-end">
          <Button className="mr-2" onClick={() => closeModal() }>Cancel</Button>
          <Button type="primary" loading={props.isRegistering} onClick={onRegister}>Submit</Button>
        </div>
      </Form>
    </>
  ))};

  const mapStateToProps = (store: RootStateOrAny) => ({
    isFetchingAll: store.rootReducer.appIPA.isFetchingAll,
    isRegistering: store.rootReducer.auth.isRegistering,
    isRegisteringCentral: store.rootReducer.auth.isRegisteringCentral,
  });

  const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
    {
      getAllDesc,
      sendRegistration,
      sendRegistrationToCentral,
      getCompanies,
    },
      dispatch,
  );

  export default connect(mapStateToProps, mapDispatchToProps)(RegistrationDetails);